import { getLeagueStatus } from "./leagueStatus";

// Which leagues get the limited slots on League Play home, and how many.
//
// Every section is capped, and past the cap the user taps "View all". One rule,
// applied everywhere, so what surfaces is predictable:
//
//   1. Active (live) first — ordered by whichever ends soonest, because that's
//      the one with a clock running on it.
//   2. Then upcoming — ordered by whichever starts soonest.
//   3. Completed only fills leftover slots — most recently finished first, so a
//      section is never empty just because nothing is running today.
//   4. Cancelled leagues never appear.
//
// Featured Cash Games is the one exception: it ignores phase entirely and orders
// by PUBLISHED date, newest first — see pickFeaturedCashGames at the bottom.
export const SECTION_CAP = 2;

const time = (value) => {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
};

// Ascending by date, rows with no usable date last.
const byDateAsc = (key) => (a, b) => {
  const ta = time(a?.[key]);
  const tb = time(b?.[key]);
  if (ta === null && tb === null) return (Number(a?.id) || 0) - (Number(b?.id) || 0);
  if (ta === null) return 1;
  if (tb === null) return -1;
  if (ta !== tb) return ta - tb;
  return (Number(a?.id) || 0) - (Number(b?.id) || 0);
};

const byDateDesc = (key) => (a, b) => -byDateAsc(key)(a, b);

// Splits a league list into the three buckets the rules above talk about.
// getLeagueStatus is the single source of truth for what state a league is in
// (it also returns null for cancelled, which is how those get dropped).
export const bucketLeagues = (games, now = new Date()) => {
  const live = [];
  const upcoming = [];
  const ended = [];

  (Array.isArray(games) ? games : []).forEach((game) => {
    if (!game) return;
    const status = getLeagueStatus(game, now);
    if (!status) return; // cancelled
    if (status.tone === "live") live.push(game);
    else if (status.tone === "ended") ended.push(game);
    else upcoming.push(game); // "soon" + "upcoming"
  });

  live.sort(byDateAsc("end_date"));
  upcoming.sort(byDateAsc("start_date"));
  ended.sort(byDateDesc("end_date"));

  return { live, upcoming, ended };
};

/**
 * The leagues a capped section shows: active, then upcoming, then completed to
 * fill.
 *
 * `includeCompleted` is false for Public Leagues: on League Play home a finished
 * league only appears under My Leagues, where it gets the results card. A
 * completed league in the public section would be a card nobody can act on.
 */
export const pickSectionLeagues = (games, limit = SECTION_CAP, includeCompleted = true) => {
  const { live, upcoming, ended } = bucketLeagues(games);
  const pool = includeCompleted ? [...live, ...upcoming, ...ended] : [...live, ...upcoming];
  return pool.slice(0, limit);
};

/**
 * Featured Cash Games: the two most recently PUBLISHED cash games, newest first.
 *
 * Client: "League Play home placement should be controlled by the Cash Game
 * published date. The most recently published Cash Game appears in the hero
 * position. When a new Cash Game is published, the current hero moves to
 * position two. The current position two game leaves League Play home but
 * remains in View All."
 *
 * So this is publication order and nothing else — not the phase-priority rule
 * the other sections use. It used to pick `hero = next upcoming` and
 * `secondary = the live game`, which is why posting a third game made one
 * silently vanish from the page with no way to reach it: whichever game did not
 * match one of those two descriptions simply had no slot, and the "View all"
 * behind it was not wired up. Ordering by published date means every game has a
 * position in one queue, the two at the front are shown, and the rest are
 * exactly what View All lists.
 *
 * `created_at` is the publish moment — a cash game goes live on creation (see
 * GameController::createGame, which sets cash games straight to "active"), so
 * there is no separate published_at to read.
 *
 * Cancelled games are still dropped: bucketLeagues is what knows that, so the
 * three buckets are merged back together here rather than re-implementing the
 * check. Completed games can hold a slot — the client always keeps one upcoming
 * and one live game up, and the day a game ends it is still the second most
 * recently published thing there is.
 */
export const pickFeaturedCashGames = (games) => {
  const { live, upcoming, ended } = bucketLeagues(games);
  const published = [...live, ...upcoming, ...ended].sort(byDateDesc("created_at"));
  return {
    hero: published[0] || null,
    secondary: published[1] || null,
  };
};
